/**
 * Who may do what, and the screen for changing it.
 *
 * ADR 001 chose Firebase Auth over Clerk and named this view as the cost of
 * that choice: Firebase has no built-in UI for assigning roles, and the wish for
 * one was the real argument for Clerk. It is the last piece of that decision.
 *
 * It renders nothing an admin has to take on trust. `setUserRole` has two safety
 * behaviours that surprise people, and both are stated here rather than
 * discovered: a demotion revokes the user's refresh tokens, so they are signed
 * out immediately instead of keeping their old permissions for up to an hour;
 * and the last admin cannot be demoted, because doing so would lock role
 * management permanently with no recovery short of a console session against
 * production.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useAuthController } from "@firecms/core";
import {
    Alert,
    Button,
    CenteredView,
    Chip,
    CircularProgress,
    Select,
    SelectItem,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
    Typography
} from "@firecms/ui";
import type { FirebaseApp } from "firebase/app";

import { ROLES, roleOf, type Role } from "../roles";
import {
    describeCallError,
    fetchUserRoles,
    setUserRole,
    type UserRoleRecord
} from "./userRoles";

const ROLE_BLURB: Record<Role, string> = {
    admin: "Everything, including categories, people, and role assignment.",
    editor: "Writes drafts and submits them for review. Cannot approve or publish.",
    reviewer: "Moves work between review and approved, and signs off claims.",
    publisher: "Publishes approved guides, unpublishes, and archives.",
    viewer: "Read only."
};

interface Notice {
    tone: "success" | "error";
    text: string;
}

function formatWhen(date: Date | undefined): string {
    if (!date) return "—";
    return date.toISOString().replace("T", " ").slice(0, 16) + " UTC";
}

export function RoleAdminView({ firebaseApp }: { firebaseApp: FirebaseApp }) {

    const authController = useAuthController();
    const callerRole = roleOf(authController);
    const callerUid = authController.user?.uid;

    const [users, setUsers] = useState<UserRoleRecord[] | undefined>(undefined);
    const [loadError, setLoadError] = useState<string | undefined>(undefined);
    const [pendingUid, setPendingUid] = useState<string | undefined>(undefined);
    const [notice, setNotice] = useState<Notice | undefined>(undefined);

    const load = useCallback(async () => {
        try {
            setUsers(await fetchUserRoles(firebaseApp));
            setLoadError(undefined);
        } catch (error) {
            /* The rules allow only an admin to read `userRoles`, so a denial here
               means the claim disagrees with what this session thinks it is. */
            setLoadError(describeCallError(error));
        }
    }, [firebaseApp]);

    useEffect(() => {
        void load();
    }, [load]);

    /* The count `setUserRole` protects. Shown so the disabled control below has
       a visible reason rather than looking broken. */
    const adminCount = useMemo(
        () => (users ?? []).filter((user) => user.role === "admin").length,
        [users]
    );

    const change = useCallback(async (user: UserRoleRecord, role: Role) => {
        setPendingUid(user.uid);
        setNotice(undefined);
        try {
            const result = await setUserRole(firebaseApp, user.uid, role);
            const who = user.email ?? user.uid;
            setNotice({
                tone: "success",
                text: result.changed
                    ? `${who} is now ${role}. Their sign-in was revoked, so the new role applies on their next sign-in rather than up to an hour from now.`
                    : `${who} was already ${role}. Nothing changed.`
            });
            await load();
        } catch (error) {
            setNotice({ tone: "error", text: describeCallError(error) });
        } finally {
            setPendingUid(undefined);
        }
    }, [firebaseApp, load]);

    if (callerRole !== "admin") {
        return (
            <CenteredView>
                <Alert color={"error"}>
                    Role assignment is admin-only. This screen is hidden from the navigation for
                    every other role, and `setUserRole` refuses the call as well, so reaching the
                    URL directly does not help.
                </Alert>
            </CenteredView>
        );
    }

    return (
        <div className={"flex flex-col gap-6 p-8 max-w-5xl mx-auto w-full"}>

            <div className={"flex flex-col gap-2"}>
                <Typography variant={"h4"}>User roles</Typography>
                <Typography variant={"body2"} className={"opacity-70"}>
                    A role is a Firebase custom claim, which is the only thing{" "}
                    <code>firestore.rules</code> can read. Changing it here calls the{" "}
                    <code>setUserRole</code> function, which sets the claim, revokes the user&apos;s
                    outstanding sessions so the change takes effect immediately, and records the
                    change in <code>cmsAuditLog</code>.
                </Typography>
            </div>

            <Alert color={"info"} size={"small"}>
                <div className={"flex flex-col gap-1"}>
                    <Typography variant={"body2"}>
                        <strong>Changing a role signs that user out.</strong> Custom claims
                        otherwise take up to an hour to reach a browser, which would leave a demoted
                        user with their old permissions until then.
                    </Typography>
                    <Typography variant={"body2"}>
                        <strong>The last admin cannot be demoted.</strong>{" "}
                        {adminCount === 1
                            ? "There is exactly one admin right now, so their control below is disabled. Promote someone else first."
                            : `There are ${adminCount} admins, so any one of them can be changed.`}
                    </Typography>
                </div>
            </Alert>

            {notice && <Alert color={notice.tone === "success" ? "success" : "error"}>{notice.text}</Alert>}

            {loadError && <Alert color={"error"}>{loadError}</Alert>}

            {users === undefined && !loadError && <CenteredView><CircularProgress/></CenteredView>}

            {users && users.length === 0 && (
                <Alert color={"warning"}>
                    No documents in <code>userRoles</code>. The mirror is written by{" "}
                    <code>setUserRole</code> and by the seed script, so a user created directly in
                    the Firebase console will not appear here until a role is assigned to them.
                </Alert>
            )}

            {users && users.length > 0 && (
                <Table>
                    <TableHeader>
                        <TableCell header scope={"col"}>User</TableCell>
                        <TableCell header scope={"col"}>Role</TableCell>
                        <TableCell header scope={"col"}>Last changed</TableCell>
                        <TableCell header scope={"col"}>Change to</TableCell>
                    </TableHeader>
                    <TableBody>
                        {users.map((user) => {
                            const isOnlyAdmin = user.role === "admin" && adminCount === 1;
                            const isSelf = user.uid === callerUid;
                            return (
                                <TableRow key={user.uid}>
                                    <TableCell>
                                        <div className={"flex flex-col"}>
                                            <Typography variant={"body2"}>
                                                {user.email ?? user.uid}
                                            </Typography>
                                            <Typography variant={"caption"} className={"opacity-60"}>
                                                {user.displayName ?? "no display name"}
                                                {isSelf ? " · you" : ""}
                                            </Typography>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {user.role
                                            ? <Chip size={"small"}>{user.role}</Chip>
                                            : <Chip size={"small"} colorScheme={"orangeLighter"}>unknown</Chip>}
                                    </TableCell>
                                    <TableCell>
                                        <div className={"flex flex-col"}>
                                            <Typography variant={"caption"}>
                                                {formatWhen(user.updatedAt)}
                                            </Typography>
                                            <Typography variant={"caption"} className={"opacity-60"}>
                                                {user.updatedBy ?? "—"}
                                            </Typography>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {/* `value` is left undefined for a row whose mirror holds
                                            something that is not a role, so the placeholder shows
                                            rather than an empty controlled value. */}
                                        <Select size={"small"}
                                                {...(user.role ? { value: user.role } : {})}
                                                disabled={isOnlyAdmin || pendingUid !== undefined}
                                                placeholder={"Assign a role"}
                                                onValueChange={(next) => {
                                                    if (next !== user.role) void change(user, next as Role);
                                                }}>
                                            {ROLES.map((role) => (
                                                <SelectItem key={role} value={role}>
                                                    {role}
                                                </SelectItem>
                                            ))}
                                        </Select>
                                        {isOnlyAdmin && (
                                            <Typography variant={"caption"} className={"opacity-60"}>
                                                the only admin
                                            </Typography>
                                        )}
                                        {isSelf && !isOnlyAdmin && (
                                            <Typography variant={"caption"} className={"opacity-60"}>
                                                changing this signs you out
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}

            <div className={"flex flex-row gap-3 items-center"}>
                <Button variant={"outlined"}
                        size={"small"}
                        disabled={pendingUid !== undefined}
                        onClick={() => void load()}>
                    Reload
                </Button>
                <Typography variant={"caption"} className={"opacity-60"}>
                    {pendingUid ? "Applying…" : "Read from the userRoles mirror; the claim is authoritative."}
                </Typography>
            </div>

            <div className={"flex flex-col gap-1"}>
                <Typography variant={"subtitle2"}>What each role can do</Typography>
                {ROLES.map((role) => (
                    <Typography key={role} variant={"caption"} className={"opacity-70"}>
                        <strong>{role}</strong> — {ROLE_BLURB[role]}
                    </Typography>
                ))}
            </div>
        </div>
    );
}
